import {useState} from 'react'
import './journalExport.css'
import {getDocumentContent} from './qirtaas'
import {exportDocx,exportMarkdown,exportPdfPrint} from './documentExport'
import {REFLECTION_PROMPTS,getReflectionSources,type ReflectionSession,type ReflectiveJournal} from './reflectiveJournal'
import {buildJournalDoc,type ExportSource} from './journalExportCore'

// Reflective Journal → "Download journal": every reflection in one document, by lecture date, as Word, PDF or Markdown.
// Written-up reflections are read from their notes (read only); nothing is changed.
type Format='docx'|'pdf'|'md'
export default function JournalExport({journal,reflections}:{journal:ReflectiveJournal;reflections:ReflectionSession[]}){
 const [open,setOpen]=useState(false)
 const [status,setStatus]=useState('')
 const [busy,setBusy]=useState(false)
 // A PDF opens in a new tab, which browsers only allow straight from a click: once ready, a second click opens it.
 const [pdf,setPdf]=useState<{content:Record<string,unknown>;title:string}|null>(null)
 async function run(format:Format){
  setOpen(false);setBusy(true);setPdf(null);setStatus(`Preparing ${reflections.length} reflection${reflections.length===1?'':'s'}…`)
  try{
   const rich=new Map<string,unknown>(),sources=new Map<string,ExportSource[]>(),queue=[...reflections]
   let done=0
   const worker=async()=>{for(let r=queue.shift();r;r=queue.shift()){
    const [doc,src]=await Promise.all([
     r.content_document_id?getDocumentContent(r.content_document_id).then(d=>d.content).catch(()=>null):Promise.resolve(undefined),
     getReflectionSources(r.id).then(x=>x.sources).catch(()=>[]),
    ])
    if(r.content_document_id)rich.set(r.id,doc??null)
    sources.set(r.id,src as ExportSource[])
    setStatus(`Reading reflection ${++done} of ${reflections.length}…`)
   }}
   await Promise.all([worker(),worker()])
   const built=buildJournalDoc({title:journal.title||'Reflective Journal',word_count_target:journal.word_count_target,deadline:journal.deadline,reflections:reflections as never,prompts:REFLECTION_PROMPTS.map(p=>({field:p.field,label:p.label})),rich,sources})
   const content=built.doc as unknown as Record<string,unknown>,title=journal.title||'Reflective Journal'
   if(format==='docx')await exportDocx(content,title)
   else if(format==='md')exportMarkdown(content,title)
   else setPdf({content,title})
   const unread=[...rich.values()].filter(v=>v===null).length
   setStatus(`${built.reflections} reflections · ${built.words.toLocaleString('en-GB')} words${unread?` · ${unread} written-up reflection${unread===1?'':'s'} could not be read (marked in the file)`:''}`)
  }catch(e:any){setStatus(e?.message||'Could not prepare the journal. Try again.')}
  finally{setBusy(false)}
 }
 return <span className="journal-export">
  <button type="button" className="journal-edit-brief" disabled={busy||!reflections.length} aria-expanded={open} onClick={()=>setOpen(o=>!o)}>{busy?'Preparing…':'Download journal'}</button>
  {open&&<span className="journal-export-menu" role="menu">
   <button type="button" role="menuitem" onClick={()=>void run('docx')}>Word (.docx)</button>
   <button type="button" role="menuitem" onClick={()=>void run('pdf')}>PDF (print)</button>
   <button type="button" role="menuitem" onClick={()=>void run('md')}>Markdown</button>
  </span>}
  {pdf&&<button type="button" className="journal-edit-brief" onClick={()=>{try{exportPdfPrint(pdf.content,pdf.title);setPdf(null)}catch(e:any){setStatus(e?.message||'Could not open the PDF.')}}}>Open PDF to print or save</button>}
  {status&&<span className="journal-export-status" role="status">{status}</span>}
 </span>
}
